import { Alert, Autocomplete, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listOptionGroups, setProductAxes } from '../../api/business/endpoints';
import type { OptionGroup, ProductDetail } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { useT } from '../../i18n/LanguageContext';

type ProductAxesDialogProps = {
  companyId: string;
  product: ProductDetail;
  onClose: () => void;
};

/**
 * Which option groups a product varies by, in order (ADR-0010 rule 8). A group added while the variants have
 * values asks which value they all take; removing one the variants differ by is refused by the API.
 */
export const ProductAxesDialog = ({ companyId, product, onClose }: ProductAxesDialogProps) => {
  const t = useT();
  const navigate = useNavigate();
  const groups = useQuery({
    queryKey: ['option-groups', companyId],
    queryFn: () => listOptionGroups(companyId),
  });
  const [selected, setSelected] = useState<Array<OptionGroup>>(product.optionGroups);
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);

  const valued = product.variants.some((variant) => variant.optionValues.length > 0);
  const added = selected.filter((group) => !product.optionGroups.some((axis) => axis.id === group.id));
  const needingDefault = valued ? added : [];

  const mutation = useAppMutation(
    () =>
      setProductAxes(companyId, product.id, {
        groupIds: selected.map((group) => group.id),
        defaults: Object.fromEntries(
          needingDefault.map((group) => [group.id, defaults[group.id]]).filter(([, value]) => Boolean(value)),
        ),
      }),
    ['product', 'products'],
    onClose,
  );

  const submit = (): void => {
    if (needingDefault.some((group) => !defaults[group.id])) {
      setProblem(t('variants.valueRequired'));

      return;
    }

    setProblem(null);
    mutation.mutate(undefined);
  };

  const options = (groups.data ?? []).map((group) => selected.find((one) => one.id === group.id) ?? group);

  return (
    <FormDialog
      title={t('variants.axesTitle')}
      onClose={onClose}
      onSubmit={submit}
      isBusy={mutation.isPending || groups.isLoading}
      error={mutation.error ?? groups.error}
      problem={problem}
    >
      <Typography variant="body2" color="text.secondary">
        {t('variants.axesHint')}
      </Typography>
      {!groups.isLoading && options.length === 0 ? (
        <Alert
          severity="info"
          variant="outlined"
          action={<Button onClick={() => navigate('/option-groups')}>{t('variants.manageGroups')}</Button>}
        >
          {t('variants.axesNone')}
        </Alert>
      ) : (
        <Autocomplete
          multiple
          options={options}
          value={selected}
          loading={groups.isLoading}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_event, value) => setSelected(value)}
          renderInput={(params) => <TextField {...params} label={t('variants.axes')} />}
        />
      )}
      {needingDefault.map((group) => {
        const full: OptionGroup = options.find((one) => one.id === group.id) ?? group;

        return (
          <TextField
            key={group.id}
            select
            required
            label={t('variants.axesDefault', { group: full.name })}
            helperText={t('variants.axesDefaultHint')}
            value={defaults[group.id] ?? ''}
            onChange={(e) => setDefaults({ ...defaults, [group.id]: e.target.value })}
          >
            {full.values.map((value) => (
              <MenuItem key={value.id} value={value.id}>
                {value.value}
              </MenuItem>
            ))}
          </TextField>
        );
      })}
    </FormDialog>
  );
};
